import { TOAST_AUTO_CLOSE_DURATION } from "@shared/constants/index";
import type Pubnub from "pubnub";
import { useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { handleRefetchAssetDocumentationFolders } from "../services/asset";

import { CURRENT_USER_PERMISSIONS_QUERY, CURRENT_USER_QUERY } from "~/api";
import client from "~/apollo/_client";
import useAuth from "~/components/general/_use-auth";
import { useGetChatCredentials } from "~/services/user/_query";
import { documentFoldersListener } from "~/components/header/DocumentFoldersListener";
import {
  CSV_DOWNLOADING_TOAST_ID,
  DOCUMENT_FOLDERS,
  IMPORT_UPLOADING_TOAST_ID,
  EMAIL_ATTACHMENT_DOWNLOAD_IDENTIFIER,
  EMAIL_ATTACHMENT_DOWNLOADING_TOAST_ID,
  MACHINE_DOCUMENTATION_DETACHED,
  PROCEDURE_ATTACHING_TOAST_ID,
  PROCEDURE_FINALIZING_TOAST_ID,
  PROCEDURE_PDF_DOWNLOADING_TOAST_ID,
  FINANCE_PDF_DOWNLOADING_TOAST_ID,
  PROCEDURE_TEMPLATE_DUPLICATING_TOAST_ID,
  PROCEDURE_UPDATE_IDENTIFIER,
  PROCEDURE_UPDATING_TOAST_ID,
  SENDING_EMAIL_TOAST_ID,
  SHARED_DRIVE,
  VALIDATING_ASSISTANT_FILES_TOAST_ID,
} from "~/constants";
import { MACHINE_BROADCAST_TOPICS } from "~/constants/machine";
import { NOTIFICATION_IDENTIFIERS } from "~/constants/notification-identifiers";
import {
  SUPPORT_TICKET_BROADCAST_TOPICS,
  SUPPORT_TICKET_CHAT_MESSAGE_IDENTIFIER,
  SUPPORT_TICKET_CREATED_IDENTIFIER,
  SUPPORT_TICKET_DELETED_IDENTIFIER,
  SUPPORT_TICKET_UPDATED_IDENTIFIER,
} from "~/constants/supportTickets";

const PROCEDURE_PDF_WEB_POLL_ACTIVE_KEY = "procedure-pdf-web-poll-active";

import {
  MEMBERSHIP_UPDATED_IDENTIFIER,
  WORK_ORDER_BROADCAST_TOPICS,
  WORK_ORDER_CHAT_MESSAGE_IDENTIFIER,
  WORK_ORDER_CREATED_IDENTIFIER,
  WORK_ORDER_DELETED_IDENTIFIER,
  WORK_ORDER_UPDATED_IDENTIFIER,
} from "~/constants/workOrders";
import {
  updateCacheOnTeamChange,
  updateFolderUnreadCount,
  updateProcedureInstanceCacheOnFinalized,
  updateProcedureTemplateCacheOnDuplicate,
  updateTicketCacheOnProcedureAttached,
  useLinkEmailAccount,
  useSendEmail,
} from "~/services";
import { clearRequestCache } from "~/services/request";
import { useChatStore } from "~/stores/chatStore";
import { useProceduresStore } from "~/stores/proceduresStore";
import type { UserType } from "~/types/user";
import { broadcaster } from "~/utils/_appBroadcast";

/**
 * How long one freshly-granted chat token is assumed to cover a burst of
 * membership changes. Module-level so every mount of this hook shares it — the
 * token is the client's, not a component's.
 */
const CREDENTIALS_REFETCH_THROTTLE = 10000; // 10 seconds
let lastCredentialsRefetchAt = 0;

interface UseGlobalListenerProps {
  onNotification?: (notification: {
    id: string;
    message: string;
    type: "success" | "error" | "info";
    autoClose?: number | false;
    hideProgressBar?: boolean;
  }) => void;
}

export const useGlobalListener = ({
  onNotification,
}: UseGlobalListenerProps = {}) => {
  const { messages } = useIntl();
  const location = useLocation();
  const authData = (useAuth() || {}) as { user?: UserType; chat?: Pubnub };
  const user = authData.user as UserType;
  const chatInstance = authData.chat as Pubnub;
  const { credentials, refetch: refetchChatCredentials } =
    useGetChatCredentials({ skip: !user?._id });
  const channelsRef = useRef({});
  const currentPathRef = useRef("");
  const pathMatcher = /^\/app\/work-orders\/(?<currentTicketId>.+)/;
  const matchedPath = location?.pathname?.match(pathMatcher)?.groups;

  const unreadChannelMessages = useChatStore((s) => s.unreadChannelMessages);
  const incrementUnreadChannel = useChatStore((s) => s.incrementUnreadChannel);
  const seedUnreadChannels = useChatStore((s) => s.seedUnreadChannels);
  const removeUnreadChannels = useChatStore((s) => s.removeUnreadChannels);
  const updateFinalizingInBackground = useProceduresStore(
    (s) => s.setProcedureFinalizing,
  );

  const { handleUpdateEmailCache } = useSendEmail();
  const { handleEmailAccountSync } = useLinkEmailAccount();

  useEffect(() => {
    if (unreadChannelMessages?.channels) {
      channelsRef.current = unreadChannelMessages?.channels;
    }
    if (location) {
      currentPathRef.current = matchedPath?.currentTicketId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadChannelMessages, location?.key]);

  const shouldUpdateNotificationCount = (message) => {
    // no teams info provided with message
    if (!message?.userMetadata?.teams) return true;

    // has all teams access
    if (!user?.teams?.length) return true;

    const userTeams = user.teams.map((team) => team._id);
    const teamsInMessage = JSON.parse(message.userMetadata.teams);

    for (const team of userTeams) {
      if (teamsInMessage.includes(team)) {
        return true;
      }
    }

    return false;
  };

  const showNotification = useCallback(
    (
      id: string,
      message: string,
      type: "success" | "error" | "info" = "info",
      options?: { autoClose?: number | false; hideProgressBar?: boolean },
    ) => {
      if (onNotification) {
        onNotification({
          id,
          message,
          type,
          autoClose: options?.autoClose,
          hideProgressBar: options?.hideProgressBar,
        });
      } else {
        // Fallback to basic toast if no notification handler is provided
        const toastOptions = {
          autoClose: options?.autoClose ?? TOAST_AUTO_CLOSE_DURATION,
          hideProgressBar: options?.hideProgressBar,
        };

        if (id) {
          toast.update(id, { render: message, ...toastOptions });
        } else {
          toast[type](message, toastOptions);
        }
      }
    },
    [onNotification],
  );

  const handleNewMessageNotification = useCallback(
    async (message) => {
      const existingChan = channelsRef.current?.[message?.channel];
      const currentUserIsPublisher = credentials?.uuid === message?.publisher;

      if (!shouldUpdateNotificationCount(message)) return;

      // Increment through the store rather than writing back `channelsRef`:
      // that ref is a snapshot from the last store change, so replacing the
      // whole map with it reverts anything that moved in between (a recount
      // landing, another chat being read) — and reading the old count out of it
      // can drop a concurrent bump.
      if (!currentUserIsPublisher && existingChan !== undefined) {
        incrementUnreadChannel(message.channel);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unreadChannelMessages],
  );

  const oemMessageNotificationHandler = async (message) => {
    if (
      message?.userMetadata?.isMachine &&
      message?.message?.text === MACHINE_DOCUMENTATION_DETACHED
    ) {
      const { id } = message.message.payload;
      if (location?.pathname.includes(id)) {
        if (!message.message.success) {
          showNotification(
            "",
            messages?.machines?.["detachDocumentationFail"],
            "error",
            { autoClose: false },
          );
          broadcaster.publish(
            MACHINE_BROADCAST_TOPICS.DOCUMENTATION_DETACHED,
            id,
            false,
          );

          return;
        }

        showNotification(
          "",
          messages?.machines?.["detachDocumentationSuccess"],
          "success",
          { autoClose: false },
        );
      }

      broadcaster.publish(
        MACHINE_BROADCAST_TOPICS.DOCUMENTATION_DETACHED,
        id,
        true,
      );
    }
    if (
      (message?.userMetadata?.isMachine ||
        message?.userMetadata?.isMachineTemplate ||
        message?.userMetadata?.isInventoryPart ||
        message?.userMetadata?.isCustomerPortal) &&
      [SHARED_DRIVE, DOCUMENT_FOLDERS].includes(message?.message?.text)
    ) {
      return documentFoldersListener(message);
    }

    if (message?.message?.text === NOTIFICATION_IDENTIFIERS.TEAM_CHANGE) {
      updateCacheOnTeamChange();
    }

    // The user's role was edited: fetch what it allows now, so actions it
    // lost disappear without a reload. A notice for a role the user no longer
    // holds is stale and left alone.
    if (
      message?.message?.text === NOTIFICATION_IDENTIFIERS.PERMISSIONS_CHANGE
    ) {
      const roleId = client.readQuery({ query: CURRENT_USER_QUERY })
        ?.currentUser?.roleId;
      if (!message?.message?.roleId || message.message.roleId === roleId) {
        client.query({
          query: CURRENT_USER_PERMISSIONS_QUERY,
          fetchPolicy: "network-only",
        });
      }
    }

    if (
      message?.message?.text ===
      NOTIFICATION_IDENTIFIERS.EMAIL_FOLDER_COUNT_SYNCED
    ) {
      updateFolderUnreadCount(
        message?.message?.folderToCountMap,
        message?.message?.emailAddressId,
      );
    }

    if (
      message?.userMetadata?.isProcedureTemplate &&
      message?.message?.text ===
        NOTIFICATION_IDENTIFIERS.PROCEDURE_TEMPLATE_DUPLICATE
    ) {
      if (!message?.message?.success) {
        showNotification(
          PROCEDURE_TEMPLATE_DUPLICATING_TOAST_ID,
          messages?.procedures?.["procedureDuplicateFailText"],
          "error",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
        return;
      }
      const { payload } = message?.message || {};

      showNotification(
        PROCEDURE_TEMPLATE_DUPLICATING_TOAST_ID,
        messages?.procedures?.["procedureDuplicateSuccessText"],
        "success",
        { autoClose: TOAST_AUTO_CLOSE_DURATION },
      );

      try {
        await updateProcedureTemplateCacheOnDuplicate(payload);
      } catch (error) {
        console.error(error);
      }
    }
    if (
      message?.userMetadata?.isProcedureTemplate &&
      message?.message?.text === PROCEDURE_UPDATE_IDENTIFIER
    ) {
      if (!message?.message?.success) {
        showNotification(
          PROCEDURE_UPDATING_TOAST_ID,
          messages?.procedures?.["errorUpdatingAttachedProcedures"],
          "error",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
        return;
      }

      showNotification(
        PROCEDURE_UPDATING_TOAST_ID,
        messages?.procedures?.["successUpdatingAttachedProcedures"],
        "success",
        {
          autoClose: TOAST_AUTO_CLOSE_DURATION,
          hideProgressBar: true,
        },
      );
    }
    if (
      message?.userMetadata?.isProcedure &&
      message?.message?.text === NOTIFICATION_IDENTIFIERS.PROCEDURE_ATTACH
    ) {
      if (!message?.message?.success) {
        showNotification(
          PROCEDURE_ATTACHING_TOAST_ID,
          messages?.procedures?.["procedureAttachFailed"],
          "error",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
        return;
      }
      const { payload } = message.message;
      try {
        const ticketCacheUpdated = await updateTicketCacheOnProcedureAttached(
          payload.workOrderId,
          payload.procedure,
          payload.addedBy,
        );

        if (ticketCacheUpdated) {
          showNotification(
            PROCEDURE_ATTACHING_TOAST_ID,
            messages?.procedures?.["procedureAttachSuccess"],
            "success",
            {
              autoClose: TOAST_AUTO_CLOSE_DURATION,
              hideProgressBar: true,
            },
          );
        }
      } catch (error) {
        console.error(error);
      }
    }

    if (
      message?.userMetadata?.isProcedure &&
      message?.message?.text === NOTIFICATION_IDENTIFIERS.PROCEDURE_PDF_DOWNLOAD
    ) {
      // Web poll owns toast + open for this download; PubNub is kept for mobile.
      const pollingProcedureId = sessionStorage.getItem(
        PROCEDURE_PDF_WEB_POLL_ACTIVE_KEY,
      );
      if (pollingProcedureId) {
        return;
      }

      if (!message?.message?.success) {
        showNotification(
          PROCEDURE_PDF_DOWNLOADING_TOAST_ID,
          messages?.procedures?.["procedureDownloadFailed"],
          "error",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
        return;
      } else {
        showNotification(
          PROCEDURE_PDF_DOWNLOADING_TOAST_ID,
          messages?.procedures?.["procedureDownloadSuccess"],
          "success",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
      }

      const { _id: procedureId, url } = message.message.payload;
      const { uuid } = message.userMetadata;

      const pdfDownloadRequestUuid = sessionStorage.getItem(procedureId);

      if (pdfDownloadRequestUuid && uuid && pdfDownloadRequestUuid === uuid) {
        window.open(url, "_blank");
      }
    }

    if (
      message?.userMetadata?.isFinance &&
      message?.message?.text === NOTIFICATION_IDENTIFIERS.FINANCE_PDF_DOWNLOAD
    ) {
      if (!message?.message?.success) {
        showNotification(
          FINANCE_PDF_DOWNLOADING_TOAST_ID,
          messages?.procedures?.["procedureDownloadFailed"],
          "error",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
        return;
      }

      showNotification(
        FINANCE_PDF_DOWNLOADING_TOAST_ID,
        messages?.procedures?.["procedureDownloadSuccess"],
        "success",
        { autoClose: TOAST_AUTO_CLOSE_DURATION },
      );

      const { _id: documentId, url } = message.message.payload;
      const { uuid } = message.userMetadata;
      const pdfDownloadRequestUuid = sessionStorage.getItem(documentId);

      if (pdfDownloadRequestUuid && uuid && pdfDownloadRequestUuid === uuid) {
        window.open(url, "_blank");
      }
    }

    if (
      message?.userMetadata?.isEmail &&
      message?.message?.text === NOTIFICATION_IDENTIFIERS.SEND_EMAIL
    ) {
      if (!message?.message.success) {
        showNotification(
          SENDING_EMAIL_TOAST_ID,
          messages?.emails?.["sendEmailFailed"],
          "error",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
        return;
      }

      showNotification(
        SENDING_EMAIL_TOAST_ID,
        messages?.emails?.["emailSentSuccess"],
        "success",
        { autoClose: TOAST_AUTO_CLOSE_DURATION },
      );
    }

    if (
      message?.message?.text ===
      NOTIFICATION_IDENTIFIERS.AI_ASSISTANT_VALIDATION_UPDATE
    ) {
      if (!message?.message.success) {
        showNotification(
          VALIDATING_ASSISTANT_FILES_TOAST_ID,
          messages?.aiAssistant?.["failureValidatedFilesToBeIndexed"],
          "error",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
        return;
      }

      showNotification(
        VALIDATING_ASSISTANT_FILES_TOAST_ID,
        messages?.aiAssistant?.["successfullyValidatedFilesToBeIndexed"],
        "success",
        { autoClose: TOAST_AUTO_CLOSE_DURATION },
      );
    }

    if (
      message?.message?.text ===
      NOTIFICATION_IDENTIFIERS.MACHINE_DOCUMENTATION_ATTACHED
    ) {
      handleRefetchAssetDocumentationFolders();
    }

    if (
      message?.userMetadata?.isEmail &&
      message?.message?.text ===
        NOTIFICATION_IDENTIFIERS.SEND_EMAIL_UPDATE_CACHE
    ) {
      handleUpdateEmailCache({ payload: message?.message?.payload });
    }

    if (
      message?.userMetadata?.isEmail &&
      message?.message?.text ===
        NOTIFICATION_IDENTIFIERS.EMAIL_ACCOUNT_SYNCED &&
      message?.message?.success
    ) {
      handleEmailAccountSync({ payload: message?.message });
    }

    if (
      message?.userMetadata?.isCsvExport &&
      message?.message?.text === NOTIFICATION_IDENTIFIERS.CSV_DOWNLOAD
    ) {
      if (!message?.message?.success) {
        showNotification(
          CSV_DOWNLOADING_TOAST_ID,
          messages.common["csvDownloadFailed"],
          "error",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
        return;
      }

      toast.dismiss(CSV_DOWNLOADING_TOAST_ID);

      const { uuid, url } = message.message.payload;

      const csvDownloadRequestUuid = sessionStorage.getItem(uuid);

      if (csvDownloadRequestUuid && uuid && csvDownloadRequestUuid === uuid) {
        window.open(url, "_blank");
      }
    }
    if (
      message?.userMetadata?.isProcedure &&
      message?.message?.text === NOTIFICATION_IDENTIFIERS.PROCEDURE_FINALIZE
    ) {
      if (!message?.message?.success) {
        showNotification(
          PROCEDURE_FINALIZING_TOAST_ID,
          messages?.procedures?.["instanceFinalizeSaveFail"],
          "error",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
        updateFinalizingInBackground(false);

        return;
      }

      const payload = message?.message?.payload;
      try {
        await updateProcedureInstanceCacheOnFinalized(payload);
        updateFinalizingInBackground(false);
        showNotification(
          PROCEDURE_FINALIZING_TOAST_ID,
          messages?.procedures?.["instanceFinalizeSaveSuccess"],
          "success",
          { autoClose: TOAST_AUTO_CLOSE_DURATION },
        );
      } catch (error) {
        console.error(error);
        updateFinalizingInBackground(false);
      }
    }
    if (
      message?.userMetadata?.isFileImporter &&
      message?.message?.text === NOTIFICATION_IDENTIFIERS.FILE_IMPORT
    ) {
      const isSuccessful = message?.message?.success;
      const errorMessage = message?.message?.error
        ?.toString()
        ?.toLowerCase()
        ?.includes("plan")
        ? message?.message?.error
        : messages?.dataManagement?.["dataUploadFailed"];

      showNotification(
        // `dromoId` is a legacy payload key; it holds the S3 key of the upload.
        `${IMPORT_UPLOADING_TOAST_ID}-${message?.message?.payload?.dromoId}`,
        isSuccessful
          ? messages?.dataManagement?.["dataUploaded"]
          : errorMessage,
        isSuccessful ? "success" : "error",
        { autoClose: TOAST_AUTO_CLOSE_DURATION },
      );
    }

    if (
      message?.userMetadata?.isEmail &&
      message?.message?.text === EMAIL_ATTACHMENT_DOWNLOAD_IDENTIFIER
    ) {
      if (!message?.message?.success) {
        showNotification(
          EMAIL_ATTACHMENT_DOWNLOADING_TOAST_ID,
          messages?.procedures?.["procedureDownloadFailed"],
          "error",
          { autoClose: 5000 },
        );
        return;
      }

      toast.dismiss(EMAIL_ATTACHMENT_DOWNLOADING_TOAST_ID);
      toast.done(EMAIL_ATTACHMENT_DOWNLOADING_TOAST_ID);

      const { url } = message.message.payload;
      const { uuid } = message.userMetadata;

      const fileDownloadRequestUuid = sessionStorage.getItem(uuid);

      if (fileDownloadRequestUuid) {
        sessionStorage.removeItem(uuid);
        window.open(url, "_blank");
      }
    }
    if (
      message?.userMetadata?.isTicket &&
      (message?.message?.text === WORK_ORDER_UPDATED_IDENTIFIER ||
        message?.message?.text === WORK_ORDER_CREATED_IDENTIFIER)
    ) {
      const {
        oemId,
        ticketId,
        userId,
        ticketStatus = "",
        customerId,
      } = message.message.payload;
      if (
        userId &&
        userId === user?._id &&
        message?.message?.text !== WORK_ORDER_CREATED_IDENTIFIER
      )
        return;
      broadcaster.publish(
        WORK_ORDER_BROADCAST_TOPICS.REORDER_WORK_ORDER_LIST,
        ticketId,
        message?.message?.text === WORK_ORDER_CREATED_IDENTIFIER,
        false,
        ticketStatus,
      );
      // Add channels to unreadChannelMessages
      const ticketChannel = `channel-${oemId}-${customerId}-${ticketId}`;
      const internalTicketChannel = `channel-${oemId}-${ticketId}-internal-notes`;
      seedUnreadChannels(
        [customerId ? ticketChannel : undefined, internalTicketChannel].filter(
          Boolean,
        ) as string[],
      );
    }

    // `isRequest` alone does NOT identify a ticket event. Chat sends carry it
    // too — ChatActions publishes `meta: { isRequest: isRequestsPage }`, so
    // every message a portal user types arrives here with the flag set and a
    // chat payload (`{ messageType, text }`) that has no `payload` at all.
    // Destructuring it threw, and because this handler is one function the
    // throw took every branch below it with it — including the chat-message
    // recount, which is why a Support Ticket's badge stopped incrementing the
    // moment the sender was on a portal page.
    if (
      message?.userMetadata?.isRequest &&
      (message?.message?.text === WORK_ORDER_CREATED_IDENTIFIER ||
        message?.message?.text === WORK_ORDER_UPDATED_IDENTIFIER)
    ) {
      clearRequestCache();
      const { oemId, ticketId, customerId } = message.message.payload ?? {};
      const ticketChannel = `channel-${oemId}-${customerId}-${ticketId}`;
      if (oemId && ticketId && customerId) seedUnreadChannels([ticketChannel]);
    }

    const supportTicketIdentifier = message?.userMetadata?.isSupportTicket
      ? message?.message?.text
      : undefined;
    if (
      supportTicketIdentifier === SUPPORT_TICKET_CREATED_IDENTIFIER ||
      supportTicketIdentifier === SUPPORT_TICKET_UPDATED_IDENTIFIER ||
      supportTicketIdentifier === SUPPORT_TICKET_DELETED_IDENTIFIER
    ) {
      const {
        ticketId,
        userId,
        ticketStatus = "",
        channels,
        ownersAndAdminsOnly,
      } = message.message.payload ?? {};
      const isCreated =
        supportTicketIdentifier === SUPPORT_TICKET_CREATED_IDENTIFIER;
      const isDeleted =
        supportTicketIdentifier === SUPPORT_TICKET_DELETED_IDENTIFIER;

      // Own updates are already reflected locally; own creations still need the
      // list reorder (mirrors the Work Order branch above). Scoped to this
      // branch rather than returning, so later branches still see the message.
      const isOwnUpdate = userId && userId === user?._id && !isCreated;

      /**
       * An escalation nobody has picked up is owners' and admins' to triage, and
       * the crew cannot open it until someone is assigned. The OEM has ONE
       * notification channel for all four roles, so the server marks the event
       * and the client drops what is not for it — otherwise this branch splices
       * the ticket into a supervisor's cached list and seeds its chat channels
       * into the unread map, where every message then climbs a badge they can
       * never clear.
       *
       * A presentation filter, not a boundary: the messages reach every OEM
       * client regardless, since the chat channel sits in an OEM-wide channel
       * group. It decides what is counted and shown, not what is delivered.
       */
      const forOthersOnly =
        ownersAndAdminsOnly && !["OWNER", "OEM"].includes(user?.role as string);

      if (!isOwnUpdate && !forOthersOnly) {
        broadcaster.publish(
          SUPPORT_TICKET_BROADCAST_TOPICS.REORDER_SUPPORT_TICKET_LIST,
          ticketId,
          isCreated,
          isDeleted,
          ticketStatus ?? "",
        );

        // Take the channel names from the payload — never derive the internal
        // one from the external one. The external name carries a company
        // segment the internal one does not, so substituting the suffix yields
        // a channel that does not exist and a permanently-zero badge with no
        // error. The external array is empty for company-less tickets.
        const ticketChannels = [
          ...(channels?.supportTicketChatChannels ?? []),
          ...(channels?.supportTicketInternalNotesChatChannels ?? []),
        ].filter(Boolean);

        // A deleted ticket keeps its PubNub membership, so its unread messages
        // would keep inflating the sidebar total with no card left to account
        // for them. Drop the channels instead of seeding them.
        if (isDeleted) removeUnreadChannels(ticketChannels);
        else seedUnreadChannels(ticketChannels);
      }
    }

    if (
      message?.userMetadata?.isTicket &&
      message?.message?.text === WORK_ORDER_DELETED_IDENTIFIER
    ) {
      const { ticketId, userId } = message.message.payload;
      if (userId && userId === user?._id) return;
      broadcaster.publish(
        WORK_ORDER_BROADCAST_TOPICS.REORDER_WORK_ORDER_LIST,
        ticketId,
        false,
        true,
      );
    }

    // A message was posted in a ticket's external chat, signalled out-of-band on
    // the connection notification channel. An open chat window already receives
    // the message directly (the user is a member of the chat channel); this
    // event exists for the badge when the chat is closed — and for the portal,
    // which has no other way to learn about it.
    //
    // Recount rather than increment: `refreshChannelsUnread` re-reads the
    // authoritative count from PubNub, so it is idempotent. A blind +1 would
    // double-count whenever the direct channel delivery lands as well, and a
    // dual-role user receives this event twice (OEM copy + connection copy).
    const chatMessageIdentifier = message?.userMetadata?.isSupportTicket
      ? SUPPORT_TICKET_CHAT_MESSAGE_IDENTIFIER
      : WORK_ORDER_CHAT_MESSAGE_IDENTIFIER;
    if (
      (message?.userMetadata?.isTicket ||
        message?.userMetadata?.isSupportTicket) &&
      message?.message?.text === chatMessageIdentifier
    ) {
      const { messageChannel, publisherUuid, userId, channels } =
        message.message.payload ?? {};

      // Our own message — the send path already marked the channel read.
      const isOwnMessage =
        (publisherUuid && publisherUuid === credentials?.uuid) ||
        (userId && userId === user?._id);

      if (!isOwnMessage) {
        // `messageChannel` is the channel the message actually landed on and is
        // all a recount needs. Fall back to the payload's channel list (never
        // to name derivation — the external name carries a company segment the
        // internal one does not).
        const channelsToRefresh = messageChannel
          ? [messageChannel]
          : [
              ...(channels?.supportTicketChatChannels ?? []),
              ...(channels?.ticketChatChannels ?? []),
            ];

        broadcaster.publish(
          WORK_ORDER_BROADCAST_TOPICS.UNREAD_REFRESH,
          channelsToRefresh.filter(Boolean),
        );
      }
    }

    // The user's channel memberships changed — assignee/follower edits, but also
    // team, deactivation and contact-access changes that never touch a ticket
    // mutation. Without this the unread map, built by a single membership scan
    // at page load, never learns about a channel the user just gained.
    //
    // One branch serves both modules: the payload shape is identical and the
    // response is the same either way ("re-read your memberships"). The marker
    // only tells us which internal-notes format to expect; the buckets stay
    // separate downstream, and a sync touching both modules arrives as two
    // events, one per marker.
    const isMembershipMarker =
      message?.userMetadata?.isTicket || message?.userMetadata?.isSupportTicket;
    if (
      isMembershipMarker &&
      message?.message?.text === MEMBERSHIP_UPDATED_IDENTIFIER &&
      credentials?.uuid &&
      // `affectedMembers` holds chat UUIDs, not user ids — the UUID is a
      // one-way hash of the id, so this must compare against our own UUID.
      message?.message?.payload?.affectedMembers?.includes(credentials.uuid)
    ) {
      const { channel, oemId, ticketId } = message.message.payload;
      // Ticket-driven syncs carry a single `channel` (external when the ticket
      // has one, else internal); user- and contact-driven syncs carry neither
      // `channel` nor `ticketId`. An empty list is meaningful: the subscriber
      // falls back to a full membership rescan, which is the correct response
      // when we can't know which channels moved.
      const channelsToRefresh = [channel];
      if (oemId && ticketId) {
        // Derive the *internal* name from `oemId` + `ticketId`, never by
        // rewriting the external channel's suffix — the external name carries a
        // company segment the internal one does not, so substitution yields a
        // channel that does not exist. The marker picks the format rather than
        // sniffing the channel string.
        const internalChannel = message?.userMetadata?.isSupportTicket
          ? `channel-${oemId}-${ticketId}-support-internal-notes`
          : `channel-${oemId}-${ticketId}-internal-notes`;
        if (internalChannel !== channel)
          channelsToRefresh.push(internalChannel);
      }
      /**
       * A membership can be granted on a channel this client's token cannot
       * read: a rerouted ticket's channel is named after the company that RAISED
       * it, while a portal's token grants `channel-{oem}-{its own connection}-*`.
       * The backend drops the cached token whenever routing changes, but nothing
       * asks for the new one — Apollo holds the old one until it happens to
       * refetch, which is why the badge righted itself minutes later.
       *
       * Applying the new token is what then clears the refusals and rescans; see
       * `_use-auth`.
       *
       * Throttled, because one cause produces many events: a reroute toggle
       * reconciles a whole subtree and announces each ticket separately, so
       * every client on the org channel would otherwise mint a token per ticket.
       * One fresh token covers all of them — it is granted from the current
       * routing, not from the event that prompted it.
       */
      const now = Date.now();
      if (now - lastCredentialsRefetchAt > CREDENTIALS_REFETCH_THROTTLE) {
        lastCredentialsRefetchAt = now;
        void refetchChatCredentials?.();
      }

      // The subscriber drops internal-notes channels on portal request pages,
      // where reading them 403s.
      broadcaster.publish(
        WORK_ORDER_BROADCAST_TOPICS.MEMBERSHIP_UPDATED,
        channelsToRefresh.filter(Boolean),
      );
    }
  };

  useEffect(() => {
    const listener = {
      message: handleNewMessageNotification,
      file: handleNewMessageNotification,
    };
    // PubNub listeners are client-wide, not per-subscription: this handler sees
    // every message on every subscribed channel and group — the ticket chat
    // channels in `requestGroups`/`supportRequestGroups` included, so real chat
    // messages reach it alongside the org events it is written for. One
    // unexpected payload must therefore not be able to abort the whole handler
    // silently; the branches are ordered, and the unread recount is last.
    const oemMessageListener = {
      message: (message) =>
        oemMessageNotificationHandler(message).catch((error) =>
          console.error("[globalListener] oem notification handler failed", {
            text: message?.message?.text,
            channel: message?.channel,
            error,
          }),
        ),
    };

    const notificationGroup = credentials?.notificationChannelGroupName;
    const requestGroups = credentials?.requestsChannelsGroupNames ?? [];
    // Support Tickets live in their own channel groups (separate 2,000-channel
    // budget from Work Orders); one client may subscribe to both.
    const supportNotificationGroup =
      credentials?.supportNotificationChannelGroupName;
    const supportRequestGroups =
      credentials?.supportRequestsChannelsGroupNames ?? [];
    const notificationChannel = credentials?.notificationChannel;
    const orgNotificationChannel = credentials?.organizationNotificationChannel;
    // Org-level events for portal connections. Plain channels, not groups —
    // and deliberately only subscribed, never joined: a membership here would
    // put the channel in the unread map, where the sidebar aggregation counts
    // every channel it finds.
    const connectionNotificationChannels =
      credentials?.connectionNotificationChannels ?? [];

    const channelGroups = [
      notificationGroup,
      supportNotificationGroup,
      ...requestGroups,
      ...supportRequestGroups,
    ].filter((g): g is string => !!g);
    const channels = [
      notificationChannel,
      orgNotificationChannel,
      ...connectionNotificationChannels,
    ].filter((c): c is string => !!c);

    if (channelGroups.length) {
      chatInstance?.subscribe({ channelGroups, withPresence: false });
    }
    if (channels.length) {
      chatInstance?.subscribe({ channels, withPresence: false });
    }

    chatInstance?.addListener(listener);
    chatInstance?.addListener(oemMessageListener);

    return () => {
      if (channelGroups.length) {
        chatInstance?.unsubscribe({ channelGroups });
      }
      if (channels.length) {
        chatInstance?.unsubscribe({ channels });
      }
      chatInstance?.removeListener(listener);
      chatInstance?.removeListener(oemMessageListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chatInstance,
    credentials?.notificationChannelGroupName,
    credentials?.requestsChannelsGroupNames,
    credentials?.supportNotificationChannelGroupName,
    credentials?.supportRequestsChannelsGroupNames,
    credentials?.token,
    credentials?.notificationChannel,
    credentials?.organizationNotificationChannel,
    // Joined rather than passed by reference: a credentials refetch can hand
    // back an equal-but-new array, and re-running this effect would tear down
    // and rebuild every subscription.
    (credentials?.connectionNotificationChannels ?? []).join("|"),
  ]);

  return null;
};
